/**
 * Minimal Markdown subset parser shared by the Rich Text element renderers
 * on web + native, and by the chat message composer. Supports the feature
 * set the Elements Library shows: headings (`#`..`######`), unordered lists
 * (`- ` / `* `), **bold**, *italic*, `code` and [links](url). Everything
 * else is treated as plain text.
 *
 * Returns a small block/inline AST so each platform can render it natively
 * (JSX on web, RN Text/View on native) without a heavyweight dependency.
 */

export type MdInline =
  | { type: "text"; value: string }
  | { type: "bold"; value: string }
  | { type: "italic"; value: string }
  | { type: "code"; value: string }
  | { type: "link"; value: string; href: string };

export type MdBlock =
  | { type: "heading"; level: number; inlines: MdInline[] }
  | { type: "paragraph"; inlines: MdInline[] }
  | { type: "list"; items: MdInline[][] }
  // The page engine has block types for each of these, and a document that
  // loses them on the way through is worse than one that never had them:
  // a checklist flattened to bullets reads as done work.
  | { type: "ordered-list"; items: MdInline[][] }
  | { type: "todo"; items: { inlines: MdInline[]; checked: boolean }[] }
  | { type: "quote"; inlines: MdInline[] }
  /** A fenced block. `text` keeps real newlines and no inline parsing. */
  | { type: "code"; text: string; lang?: string }
  /** A pipe table. Cells are plain text — the page block stores them so. */
  | { type: "table"; columns: string[]; rows: string[][] }
  /** An image on its own line. Inline images stay part of the text. */
  | { type: "image"; url: string; alt: string }
  | { type: "divider" };

// Code FIRST, so a `*` inside backticks stays literal instead of opening
// emphasis — the whole point of marking a span as code.
const INLINE_RE =
  /(`([^`]+)`)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(\[([^\]]+)\]\(([^)]+)\))/g;

/** Parse a single line of inline markdown into its text / mark runs. */
export function parseInline(text: string): MdInline[] {
  const out: MdInline[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) {
      out.push({ type: "text", value: text.slice(last, m.index) });
    }
    if (m[2] !== undefined) {
      out.push({ type: "code", value: m[2] });
    } else if (m[4] !== undefined) {
      out.push({ type: "bold", value: m[4] });
    } else if (m[6] !== undefined) {
      out.push({ type: "italic", value: m[6] });
    } else if (m[8] !== undefined) {
      out.push({ type: "link", value: m[8], href: m[9] });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    out.push({ type: "text", value: text.slice(last) });
  }
  return out.length ? out : [{ type: "text", value: text }];
}

/** Parse a Markdown-subset string into a block AST. */
export function parseMarkdown(src: string): MdBlock[] {
  const lines = (src || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks: MdBlock[] = [];
  // One run of consecutive same-kind items at a time. A bullet list
  // interrupted by a numbered one is two blocks, which is what the author
  // wrote and what the editor would have made.
  let listItems: MdInline[][] | null = null;
  let listKind: "list" | "ordered-list" = "list";
  let todoItems: { inlines: MdInline[]; checked: boolean }[] | null = null;
  let codeLines: string[] | null = null;
  let codeLang: string | undefined;

  const flushList = () => {
    if (listItems && listItems.length) {
      blocks.push({ type: listKind, items: listItems } as MdBlock);
    }
    listItems = null;
    if (todoItems && todoItems.length) {
      blocks.push({ type: "todo", items: todoItems });
    }
    todoItems = null;
  };

  /** `| a | b |` split into its cells, or null if the line is not a row. */
  const cells = (line: string): string[] | null => {
    const trimmed = line.trim();
    if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) return null;
    return trimmed
      .slice(1, -1)
      .split("|")
      .map((c) => c.trim());
  };
  /** `| --- | :--: |` — the row that makes the one above it a header. */
  const isDivider = (line: string): boolean => {
    const parts = cells(line);
    return !!parts?.length && parts.every((c) => /^:?-{1,}:?$/.test(c));
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!;
    const line = raw.trimEnd();

    // A fence swallows everything until it closes, so a `#` or `-` inside a
    // snippet stays a `#` rather than becoming a heading.
    if (codeLines !== null) {
      if (/^\s*```/.test(line)) {
        blocks.push({
          type: "code",
          text: codeLines.join("\n"),
          ...(codeLang ? { lang: codeLang } : {}),
        });
        codeLines = null;
        codeLang = undefined;
      } else {
        codeLines.push(raw);
      }
      continue;
    }
    const fence = /^\s*```\s*([A-Za-z0-9_+-]*)\s*$/.exec(line);
    if (fence) {
      flushList();
      codeLines = [];
      codeLang = fence[1] || undefined;
      continue;
    }

    if (line.trim() === "") {
      flushList();
      continue;
    }

    // Checkbox before plain bullet: "- [ ] x" is also a valid bullet, and
    // whichever is tested first wins.
    const todoMatch = /^[-*]\s+\[([ xX])\]\s+(.*)$/.exec(line.trim());
    if (todoMatch) {
      if (listItems) flushList();
      if (!todoItems) todoItems = [];
      todoItems.push({
        inlines: parseInline(todoMatch[2]),
        checked: todoMatch[1].toLowerCase() === "x",
      });
      continue;
    }
    const listMatch = /^[-*]\s+(.*)$/.exec(line.trim());
    if (listMatch) {
      if (todoItems || (listItems && listKind !== "list")) flushList();
      listKind = "list";
      if (!listItems) listItems = [];
      listItems.push(parseInline(listMatch[1]));
      continue;
    }
    const orderedMatch = /^\d+[.)]\s+(.*)$/.exec(line.trim());
    if (orderedMatch) {
      if (todoItems || (listItems && listKind !== "ordered-list")) flushList();
      listKind = "ordered-list";
      if (!listItems) listItems = [];
      listItems.push(parseInline(orderedMatch[1]));
      continue;
    }

    // A table is a header row, a divider, then rows — recognised together,
    // because a lone `| a | b |` is just a line with pipes in it and
    // guessing otherwise turns prose into a one-column table.
    const header = cells(line);
    if (header && i + 1 < lines.length && isDivider(lines[i + 1]!)) {
      const rows: string[][] = [];
      let j = i + 2;
      for (; j < lines.length; j++) {
        const row = cells(lines[j]!);
        if (!row) break;
        rows.push(row);
      }
      flushList();
      blocks.push({ type: "table", columns: header, rows });
      i = j - 1;
      continue;
    }

    // `---` / `***` / `___`. Tested before the heading rule, since a
    // Setext underline is not part of this subset and `---` only ever
    // means a rule here.
    if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushList();
      blocks.push({ type: "divider" });
      continue;
    }

    // An image alone on a line is a block; one inside a sentence stays
    // inline text, because the page engine has no inline image either.
    const imageMatch = /^!\[([^\]]*)\]\(([^)\s]+)\)$/.exec(line.trim());
    if (imageMatch) {
      flushList();
      blocks.push({ type: "image", alt: imageMatch[1] ?? "", url: imageMatch[2]! });
      continue;
    }

    flushList();
    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line.trim());
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        inlines: parseInline(headingMatch[2]),
      });
      continue;
    }
    const quoteMatch = /^>\s?(.*)$/.exec(line.trim());
    if (quoteMatch) {
      blocks.push({ type: "quote", inlines: parseInline(quoteMatch[1]) });
      continue;
    }
    blocks.push({ type: "paragraph", inlines: parseInline(line) });
  }
  // An unclosed fence still yields its block rather than vanishing.
  if (codeLines !== null) {
    blocks.push({
      type: "code",
      text: codeLines.join("\n"),
      ...(codeLang ? { lang: codeLang } : {}),
    });
  }
  flushList();
  return blocks;
}
